import { ColorIcon, colors, HStack, VLine, VStack } from "goi_common";
import { useRouter } from "next/router";
import { ReactNode, useMemo } from "react";

import { ObituarySanju } from "../../type";
import { useGetObituary } from "../hooks/useGetObituary";
import { BugoSection } from "./BugoSection";

const SANGJU_PER_ROW_WITH_PHONE = 3;
const SANGJU_PER_ROW_WITHOUT_PHONE = 4;

const BugoFormSangjuPreviewPlaceholder = () => {
  return (
    <VStack gap="20px">
      <BugoFormPreviewItemPlaceholder left="대표상주" right="(예시) 김상주" />
      <BugoFormPreviewItemPlaceholder left="아들" right="(예시) 김아들" />
      <BugoFormPreviewItemPlaceholder left="딸" right="(예시) 김자녀" />
    </VStack>
  );
};

export const BugoSangjuPreview = ({
  obituaryKey,
  readOnly,
}: {
  obituaryKey?: string;
  readOnly: boolean;
}) => {
  const router = useRouter();

  const { data } = useGetObituary();

  const sangjuGroup = useMemo(() => {
    if (!data?.sangjus || data.sangjus.length < 1) return null;

    return data.sangjus.reduce((acc, sangju) => {
      acc[sangju.relation] = acc[sangju.relation] || [];
      acc[sangju.relation].push(sangju);
      return acc;
    }, {} as Record<string, ObituarySanju[]>);
  }, [data?.sangjus]);

  const isSomeValidValue = data?.sangjus?.some((sangju) => sangju.name);
  const actionButton = !readOnly ? (
    <HStack
      as="button"
      gap="4px"
      alignItems="center"
      className="body_14_m"
      css={{
        width: "fit-content",
        padding: "4px 12px",
        borderRadius: "20px",
        border: `1px solid ${colors.gray500}`,
      }}
      onClick={() => {
        const path = `/bugo/update/sangju${
          obituaryKey ? `?obituaryKey=${obituaryKey}` : ""
        }`;
        router.push(path);
      }}
    >
      <ColorIcon name="icon-pencil" size="16px" color={colors.black} />
      {isSomeValidValue ? "정보 수정하기" : "정보 입력하기"}
    </HStack>
  ) : undefined;

  return (
    <BugoSection title="상주" action={actionButton}>
      <VStack gap="20px">
        {sangjuGroup ? (
          <BugoSangjuPreviewItems sangjuGroup={sangjuGroup} />
        ) : (
          <BugoFormSangjuPreviewPlaceholder />
        )}
      </VStack>
    </BugoSection>
  );
};

const BugoSangjuPreviewItems = ({
  sangjuGroup,
}: {
  sangjuGroup: Record<string, ObituarySanju[]>;
}) => {
  return (
    <VStack gap="20px">
      {Object.entries(sangjuGroup).map(([relationKey, value]) => {
        const hasAnyPhone = value.some((sangju) => sangju.phone);
        const perRow = hasAnyPhone
          ? SANGJU_PER_ROW_WITH_PHONE
          : SANGJU_PER_ROW_WITHOUT_PHONE;

        const chunks: ObituarySanju[][] = [];
        for (let i = 0; i < value.length; i += perRow) {
          chunks.push(value.slice(i, i + perRow));
        }

        return (
          <HStack key={relationKey} alignItems="flex-start">
            <div
              className="body_16_m gray600"
              css={{
                maxWidth: "60px",
                width: "60px",
                marginRight: "20px",
                flexShrink: 0,
              }}
            >
              {relationKey}
            </div>

            <VStack gap="8px" css={{ flex: 1, minWidth: 0 }}>
              {chunks.map((chunk, chunkIndex) => (
                <HStack key={chunkIndex} alignItems="center">
                  {chunk.map((sangju, index) => (
                    <HStack key={sangju.key} alignItems="center">
                      {index !== 0 && (
                        <VLine
                          css={{
                            maxHeight: "12px",
                            margin: "0px 10px",
                          }}
                          color={colors.gray300}
                        />
                      )}
                      <HStack gap="4px" alignItems="center">
                        <div className="body_16_m">{sangju.name}</div>
                        {sangju.phone && (
                          <ColorIcon
                            name="icon-call-mono"
                            size="14px"
                            color={colors.warmGray650}
                          />
                        )}
                      </HStack>
                    </HStack>
                  ))}
                </HStack>
              ))}
            </VStack>
          </HStack>
        );
      })}
    </VStack>
  );
};

interface BugoFormPreviewSectionItemProps {
  left: ReactNode;
  right: ReactNode;
}

export const BugoFormPreviewItem = ({
  left,
  right,
}: BugoFormPreviewSectionItemProps) => {
  return (
    <HStack gap="20px">
      <div
        className="body_16_m gray600"
        css={{
          maxWidth: "60px",
          width: "60px",
        }}
      >
        {left}
      </div>
      <div className="body_16_m">{right}</div>
    </HStack>
  );
};

export const BugoFormPreviewItemPlaceholder = ({
  left,
  right,
}: BugoFormPreviewSectionItemProps) => {
  return (
    <HStack gap="20px">
      <div
        className="body_16_m gray600"
        css={{
          maxWidth: "60px",
          width: "60px",
        }}
      >
        {left}
      </div>
      <div
        className="body_16_m"
        css={{
          opacity: "0.3",
        }}
      >
        {right}
      </div>
    </HStack>
  );
};
